import { useRef, useEffect } from 'react';

import { keycloakLogout } from '../utils/keycloakLogout';

import {
  AUTHENTICATION_STATUSES,
  type AuthenticationStatuses,
} from '../components/PageWrappers/AdDetails/types';
import type { UserCookieInfo } from '../types';
import { isProtectedRoute } from '../constants/protectedRoutes';

/**
 * This hook is responsible for ending an authentication session if the DDUS cookie has expired.
 *
 * The duration of the session is longer than the duration of the DDUS cookie. This cookie contains user data,
 * including name, verification status, and registration date.
 *
 * If the cookie is not available, we should end the session, so that the user may sign in again
 * and have a new instance of the DDUS cookie set with the requisite data.
 */
export const useCookieExpiry = (args: {
  userCookie: UserCookieInfo | undefined;
  authenticationStatus: AuthenticationStatuses;
  environmentName: string;
}) => {
  const { userCookie, authenticationStatus, environmentName } = args;

  const logoutInProgress = useRef(false);
  const hasBeenChecked = useRef(false);

  /**
   * We check production and staging environments because DDUS may be missing altogether on other environments such as local development.
   * Not checking these environments allows users to at least log in, despite missing features associated with the cookie.
   */
  useEffect(() => {
    if (
      (environmentName === 'production' || environmentName === 'staging') &&
      authenticationStatus === AUTHENTICATION_STATUSES.AUTHENTICATED &&
      !userCookie &&
      !logoutInProgress.current &&
      !window.location.href.includes('/signin')
    ) {
      if (hasBeenChecked.current) {
        return;
      }

      hasBeenChecked.current = true;
      logoutInProgress.current = true;
      keycloakLogout(
        isProtectedRoute(location.pathname) ? '/' : undefined,
      ).finally(() => {
        logoutInProgress.current = false;
      });
    }
  }, [authenticationStatus, userCookie, environmentName]);
};
